import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage } from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const FALLBACK_DISTRICTS = [
  { id: 'bronx', label: 'BRONX', meta: 'HOME' },
  { id: 'precession_zone', label: 'PRECESSION ZONE', meta: 'SCAN +' },
  { id: 'hronir_vault', label: 'HRONIR VAULT', meta: 'SCAN ++' },
  { id: 'axioma_null', label: 'AXIOMA NULL', meta: 'SCAN +++' },
  { id: 'the_dividual_market', label: 'DIVIDUAL MARKET', meta: 'SCAN ++' },
];

const TRAVEL_TARIFF = 25;

const state = {
  selected: 1,
  districts: FALLBACK_DISTRICTS,
  loaded: false,
  loading: false,
  waiting: false,
  error: null,
  receipt: null,
};

let districtsPromise = null;

async function loadDistricts() {
  if (districtsPromise || state.loaded) return districtsPromise;
  districtsPromise = (async () => {
    try {
      const session = useCoreSession.getState();
      if (!session.token) return;
      await session.ensureSession();
      const content = await session.loadContent();
      const rows = (content?.districts || [])
        .filter((district) => district.status === 'open')
        .map((district) => {
          const scan = Number(district.scanIntensity ?? 0);
          return {
            id: district.districtId,
            label: String(district.name || district.districtId).toUpperCase(),
            meta: scan >= 3000 ? 'SCAN +++' : scan >= 2000 ? 'SCAN ++' : scan >= 1000 ? 'SCAN +' : 'HOME',
          };
        });
      if (rows.length) state.districts = rows;
      if (state.selected >= state.districts.length) state.selected = 0;
      state.loaded = true;
    } finally {
      districtsPromise = null;
    }
  })();
  return districtsPromise;
}

function text(ctx, value, x, y, color = TERMINAL.TEXT, scale = 1) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, scale);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function coreSnapshot() {
  const session = useCoreSession.getState();
  const core = session.state || {};
  return {
    status: session.status,
    cash: Number(core.cash?.amount ?? 0),
    day: session.run?.day ?? core.day ?? null,
    heat: Number(core.heat ?? 0),
    districtId: core.districtId || null,
    runActive: Boolean(session.run?.sessionId),
    loading: Boolean(session.loading),
  };
}

function travelReasons(snapshot) {
  const district = state.districts[state.selected];
  if (snapshot.status !== 'online') return ['OFFLINE'];
  if (district.id === snapshot.districtId) return ['ALREADY AT DISTRICT'];
  if (snapshot.cash < TRAVEL_TARIFF) return ['INSUFFICIENT CASH'];
  return [];
}

function waitReasons(snapshot) {
  if (snapshot.status !== 'online') return ['OFFLINE'];
  if (!snapshot.runActive) return ['NO ACTIVE RUN'];
  return [];
}

function drawTitle(ctx, safe) {
  const core = coreSnapshot();
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, 24);
  rule(ctx, 0, 23, safe.width);
  text(ctx, 'DOPEOS // ROUTE', 8, 4, TERMINAL.TEXT);
  if (safe.width >= 500) text(ctx, 'SELECT DISTRICT', 208, 4, TERMINAL.TEXT_DIM);
  text(ctx, core.status === 'online' ? 'LIVE' : 'OFFLINE', safe.width - 88, 4, core.status === 'online' ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawList(ctx, x, y, w, h, mobile = false) {
  DopeUI.Panel(ctx, x, y, w, h, 'DISTRICTS');
  text(ctx, '#  DISTRICT            META', x + 10, y + 24, TERMINAL.TEXT_DIM);
  rule(ctx, x + 8, y + 40, w - 16);

  const rowH = mobile ? 44 : 34;
  const top = y + 50;
  const core = coreSnapshot();
  state.districts.forEach((district, index) => {
    const rowY = top + index * rowH;
    const selected = index === state.selected;
    const here = district.id === core.districtId;
    const label = `${here ? '*' : ' '} ${index + 1}  ${district.label.padEnd(16)} ${district.meta}`;

    if (selected) {
      ctx.fillStyle = TERMINAL.GREEN_DARK;
      ctx.fillRect(x + 6, rowY - 4, w - 12, rowH - 4);
    }
    text(ctx, selected ? '>' : ' ', x + 10, rowY, TERMINAL.TEXT_BRIGHT);
    text(ctx, label, x + 22, rowY, selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);

    interactiveRegistry.register({
      id: `route-district-${district.id}`,
      x: x + 6,
      y: rowY - 4,
      w: w - 12,
      h: rowH,
      onClick: () => {
        state.selected = index;
        state.error = null;
        state.receipt = null;
      },
    });
  });
}

function drawTravel(ctx, x, y, w, h, compact = false) {
  const core = coreSnapshot();
  const district = state.districts[state.selected];
  const travelDisabled = travelReasons(core).length > 0 || state.loading;
  const waitDisabled = waitReasons(core).length > 0 || state.loading;
  DopeUI.Panel(ctx, x, y, w, h, 'TRAVEL');

  text(ctx, 'DEST', x + 10, y + 28, TERMINAL.TEXT_DIM);
  text(ctx, district.label, x + 76, y + 28, TERMINAL.TEXT_BRIGHT);
  text(ctx, 'TARIFF', x + 10, y + 50, TERMINAL.TEXT_DIM);
  text(ctx, `$${TRAVEL_TARIFF.toFixed(2)}`, x + 76, y + 50, TERMINAL.TEXT);
  text(ctx, 'CASH', x + 10, y + 70, TERMINAL.TEXT_DIM);
  text(ctx, core.status === 'online' ? `$${core.cash.toFixed(2)}` : '--', x + 76, y + 70, TERMINAL.TEXT);
  text(ctx, 'DAY', x + 10, y + 90, TERMINAL.TEXT_DIM);
  text(ctx, core.day == null ? '--' : `${core.day}/30`, x + 76, y + 90, TERMINAL.TEXT);
  text(ctx, 'HEAT', x + 10, y + 110, TERMINAL.TEXT_DIM);
  text(ctx, core.status === 'online' ? `${core.heat}/1000` : '--', x + 76, y + 110, TERMINAL.TEXT);
  text(ctx, 'CURRENT', x + 10, y + 130, TERMINAL.TEXT_DIM);
  text(ctx, (core.districtId || '--').toUpperCase(), x + 76, y + 130, TERMINAL.TEXT_DIM);

  const message = state.error
    || state.receipt?.detail
    || travelReasons(core)[0]
    || 'TRAVEL READY';
  if (!compact) text(ctx, `// ${message}`.slice(0, 36), x + 10, y + 154, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);

  const gap = 8;
  const btnW = (w - 20 - gap) / 2;
  const btnH = compact ? 44 : 26;
  const btnY = y + h - btnH - 12;
  DopeUI.Button(ctx, x + 10, btnY, btnW, btnH, state.loading && !state.waiting ? 'TRAVELING' : 'TRAVEL', travelDisabled ? 'disabled' : 'hover');
  if (!travelDisabled) {
    interactiveRegistry.register({
      id: 'route-travel',
      x: x + 10,
      y: btnY,
      w: btnW,
      h: btnH,
      onClick: async () => {
        state.loading = true;
        state.waiting = false;
        state.error = null;
        try {
          const result = await useCoreSession.getState().executeTravel(district.id);
          state.receipt = { detail: `ARRIVED ${district.label} // DAY ${result.run?.day ?? '--'} // HEAT ${result.state?.heat ?? '--'}` };
        } catch (error) {
          state.error = coreErrorMessage(error);
        } finally {
          state.loading = false;
        }
      },
    });
  }
  const waitX = x + 10 + btnW + gap;
  DopeUI.Button(ctx, waitX, btnY, btnW, btnH, state.loading && state.waiting ? 'LAYING LOW' : 'LAY LOW', waitDisabled ? 'disabled' : 'hover');
  if (!waitDisabled) {
    interactiveRegistry.register({
      id: 'route-wait',
      x: waitX,
      y: btnY,
      w: btnW,
      h: btnH,
      onClick: async () => {
        state.loading = true;
        state.waiting = true;
        state.error = null;
        try {
          const result = await useCoreSession.getState().waitTurn();
          const day = Number.isFinite(Number(result?.turnsRemaining))
            ? 30 - Number(result.turnsRemaining)
            : (result?.day ?? result?.run?.day ?? '--');
          state.receipt = { detail: `LAY LOW // DAY ${day} // HEAT ${coreSnapshot().heat}` };
        } catch (error) {
          state.error = coreErrorMessage(error);
        } finally {
          state.loading = false;
        }
      },
    });
  }
}

function drawFooter(ctx, safe) {
  const mobile = safe.width < 500;
  const footerY = mobile ? 528 : 320;
  rule(ctx, 0, footerY, safe.width);
  text(ctx, 'TAB FOCUS   ENTER SELECT   ESC DESKTOP', 8, mobile ? footerY + 10 : 330, TERMINAL.TEXT_DIM);
  text(ctx, 'AUDIT 150+ // LAY LOW -5 // BRIBE -50%', 8, mobile ? footerY + 26 : 356, TERMINAL.GREEN);
  const backX = mobile ? 8 : 400;
  const backY = mobile ? 590 : 326;
  const backW = mobile ? safe.width - 16 : safe.width - 408;
  const backH = mobile ? 44 : 24;
  DopeUI.Button(ctx, backX, backY, backW, backH, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'route-back', x: backX, y: backY, w: backW, h: backH,
    onClick: () => navigateTo('main_menu'),
  });
  if (!mobile) text(ctx, 'DOPE CHAIN WARS // BRONX', safe.width - 216, 384, TERMINAL.GREEN_DARK);
}

registerView('route', function renderRoute(ctx, time, width, height) {
  interactiveRegistry.clear();
  const session = useCoreSession.getState();
  if (session.status === 'online' && session.token && !state.loaded && !districtsPromise) {
    loadDistricts();
  }
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  drawTitle(ctx, safe);
  const mobile = safe.width < 500;
  if (mobile) {
    drawList(ctx, 8, 32, safe.width - 16, 270, true);
    drawTravel(ctx, 8, 304, safe.width - 16, 216, true);
  } else {
    drawList(ctx, 8, 40, 376, 272);
    drawTravel(ctx, 400, 40, 232, 272);
  }
  drawFooter(ctx, safe);
});

export default 'route';
